import { IngredientsContext } from "@/app/clientLayout";
import useIndexedDB from "@/lib/useIndexedDB";
import useLocalStorageState from "@/lib/useLocalStorageState";
import { AcademicCapIcon } from "@heroicons/react/24/outline";
import ErrorMessage from "@/components/ErrorMessage";
import { CurrentUser, DiscoverContext, UserContext } from "@/components/Page";
import UndrawEmptyStreet from "@/components/svg/UndrawEmptyStreet";
import {
  combineUnits,
  formatAmount,
  getUnitOrFallback,
  toRelativeAmount,
} from "@/lib/measurement";
import useDebounce from "@/lib/useDebounce";
import { classNames } from "@/lib/util";
import mixpanel from "mixpanel-browser";
import Link from "next/link";
import {
  AllRecipesQuery,
  RecipeQuery,
  useAllRecipesQuery,
} from "@/queries/autogenerate/graphql";
import React, { useContext, useRef } from "react";
import toast from "react-hot-toast";
import styled from "styled-components";
import { useClient } from "urql";
import { getRecipeSections } from "../RecipesPage/util";
import { TextContentWrapper } from "../styles/TextContentWrapper";
import ExploreIcon from "../svg/ExploreIcon";
import FinalisePartialRecipe, { PartialRecipe } from "./FinalisePartialRecipe";
import { mapify } from "./util";

const SearchForm = styled.form`
  width: 400px;
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  /* padding: 5px; */
  margin-bottom: 10px;

  label {
    flex: 0;
    font-size: small;
  }

  /* input {
    padding: 5px;
    min-height: 38px;
    width: 300px;
    border: 1px solid hsl(0, 0%, 80%);
    border-radius: 4px;
  } */
`;
interface OuterProps {
  defaultRecipe?: NonNullable<RecipeQuery["recipe"]>;
  setSearchFn?: (value: string) => void;
  onCompleted?: () => void;
}

const CloneRecipe: React.FC<OuterProps> = (props) => {
  return (
    <UserContext.Consumer>
      {(user) => (
        <DiscoverContext.Consumer>
          {({ popularRecipes, recentRecipes }) => (
            <CloneRecipeInner
              {...{
                ...props,
                user,
                popularRecipes,
                recentRecipes,
              }}
            />
          )}
        </DiscoverContext.Consumer>
      )}
    </UserContext.Consumer>
  );
};

interface Props extends OuterProps {
  user: CurrentUser;
  popularRecipes: AllRecipesQuery["allRecipes"];
  recentRecipes: AllRecipesQuery["allRecipes"];
}

const CloneRecipeInner: React.FC<Props> = ({
  defaultRecipe,
  setSearchFn,
  onCompleted,
  user,
  popularRecipes,
  recentRecipes,
}) => {
  const client = useClient();

  const { setIngredients } = useContext(IngredientsContext);

  const [selectedRecipe, setSelectedRecipe] = useIndexedDB<string | null>(
    "cloneRecipeSelectedRecipe",
    null,
    !user.saveRecipeProgress
  );
  const [templateRecipe, setTemplateRecipe] = useIndexedDB<
    PartialRecipe[] | null
  >("cloneRecipeTemplate3", null, !user.saveRecipeProgress);

  const [search, setSearch] = useIndexedDB(
    "cloneRecipeSearch",
    "",
    !user.saveRecipeProgress
  );

  const debouncedSearch = useDebounce(search, 300);

  const textInput = useRef<HTMLInputElement>(null);

  const reset = () => {
    setSelectedRecipe(null);
    setTemplateRecipe(null);
    setSearch("");
    if (setSearchFn) {
      setSearchFn("");
    }
  };

  const [amountNumberFormat] = useLocalStorageState(
    "amountNumberFormat",
    "decimal"
  );

  // Searched Recipes
  const [{ fetching: fetching, error: error, data: data }] = useAllRecipesQuery(
    {
      variables: {
        where: {
          deletedAt: null,
          public: { equals: true },
          school: {
            isNot: { id: { equals: user.school?.id } },
          },
          // inspired: {
          //   some: {
          //     deletedAt: null,
          //   },
          // },
          // image: { not: null },
          method: { not: null },
          OR: [
            { name: { contains: debouncedSearch } },
            {
              tags: {
                some: {
                  name: { contains: debouncedSearch },
                },
              },
            },
            {
              recipeIngredients: {
                some: {
                  ingredient: {
                    is: {
                      name: {
                        contains: debouncedSearch,
                      },
                    },
                  },
                },
              },
            },
            {
              recipeInformation: {
                some: {
                  contents: {
                    contains: debouncedSearch,
                  },
                },
              },
            },
          ],
        },
        take: 30,
      },
    }
  );

  const searchedRecipes = data?.allRecipes ?? [];
  // const popularRecipes = useMemo(
  //   () => dataPopular?.allRecipes.sort(() => Math.random() - 0.5) ?? [],
  //   [dataPopular]
  // );
  // const recentRecipes = useMemo(
  //   () => dataRecent?.allRecipes.sort(() => Math.random() - 0.5) ?? [],
  //   [dataRecent]
  // );

  // console.group("keeping track");
  // console.log("selectedRecipe", selectedRecipe);
  // console.log("templateRecipe", templateRecipe);
  // console.groupEnd();

  // don't provide an option to set the templateRecipe if a defaultRecipe is provided

  if (error) return <ErrorMessage error={error} />;

  return (
    <div className="w-full">
      {!selectedRecipe ? (
        <>
          <div
            className={`overflow-hidden transition-all ${
              search ? "h-12 opacity-100" : "h-0 opacity-0"
            }`}
          >
            <button className="btn" onClick={reset}>
              Reset
            </button>
          </div>

          <h2 className="mb-5 flex items-center justify-center">
            <ExploreIcon className="mr-2 w-5 text-gray-600" /> Discover Shared
            Recipes
          </h2>

          <SearchForm
            onSubmit={async (e) => {
              e.preventDefault();
            }}
            className="mx-auto"
          >
            <input
              ref={textInput}
              type="text"
              name="clone_recipe_search_input"
              id="clone_recipe_search_input"
              placeholder="Cakes"
              className="w-full rounded border border-solid border-gray-300 p-2.5 text-2xl transition hover:border-gray-400"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (setSearchFn) {
                  setSearchFn(e.target.value);
                }
              }}
              autoFocus
            />
          </SearchForm>

          {debouncedSearch ? (
            <>
              {fetching ? <div className="spinner mx-auto my-4" /> : null}
              {!data ? null : searchedRecipes.length ? (
                <div className="mx-auto grid grid-cols-4 grid-rows-1 content-stretch items-stretch gap-8 overflow-y-hidden text-left 2xl:grid-cols-3 xl:grid-cols-2 md:grid-cols-1">
                  {searchedRecipes
                    .filter((_, index) => index < 12)
                    .map((recipe) => (
                      <RecipeCard
                        key={recipe.id}
                        recipe={recipe}
                        setSelectedRecipe={setSelectedRecipe}
                        setTemplateRecipe={setTemplateRecipe}
                        amountNumberFormat={amountNumberFormat}
                      />
                    ))}
                </div>
              ) : (
                <div className="flex flex-col pt-8">
                  <UndrawEmptyStreet width="200px" className="mx-auto my-2" />
                  <span className="text-xl">No Matching Recipes Found</span>
                </div>
              )}
            </>
          ) : (
            // Jumbotron
            <div className="xxl:max-w-6xl xlg:max-w-5xl mx-auto w-full max-w-[100rem] space-y-16 text-left">
              {popularRecipes.length ? (
                <div>
                  <h2 className="mb-2">Most Popular</h2>
                  <div
                    className="mx-auto grid grid-cols-4 grid-rows-1 content-stretch items-stretch gap-8 overflow-y-hidden 2xl:grid-cols-3 xl:grid-cols-2 md:grid-cols-1"
                    style={{ gridAutoRows: 0 }}
                  >
                    {popularRecipes.slice(0, 4).map((recipe, index) => (
                      <RecipeCard
                        key={recipe.id}
                        recipe={recipe}
                        setSelectedRecipe={setSelectedRecipe}
                        setTemplateRecipe={setTemplateRecipe}
                        amountNumberFormat={amountNumberFormat}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
              {recentRecipes.length ? (
                <div>
                  <h2 className="mb-2">Recently Shared</h2>
                  <div
                    className="mx-auto grid grid-cols-4 grid-rows-1 content-stretch items-stretch gap-8 overflow-y-hidden 2xl:grid-cols-3 xl:grid-cols-2 md:grid-cols-1"
                    style={{ gridAutoRows: 0 }}
                  >
                    {recentRecipes.slice(0, 4).map((recipe, index) => (
                      <RecipeCard
                        key={recipe.id}
                        recipe={recipe}
                        setSelectedRecipe={setSelectedRecipe}
                        setTemplateRecipe={setTemplateRecipe}
                        amountNumberFormat={amountNumberFormat}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </>
      ) : !templateRecipe ? (
        <>
          <div className="spinner size-20" />
        </>
      ) : (
        <>
          {!defaultRecipe ? (
            <button
              className="btn btn-warning"
              onClick={() => {
                setSelectedRecipe(null);
                setTemplateRecipe(null);

                setTimeout(() => {
                  if (textInput.current != document.activeElement) {
                    textInput.current?.focus();
                  }
                }, 500);
              }}
            >
              Select a Different Recipe
            </button>
          ) : null}
          {templateRecipe ? (
            <FinalisePartialRecipe
              initialRecipes={templateRecipe}
              updateRecipes={setTemplateRecipe}
              onCompleted={(recipeIDs) => {
                mixpanel.track("Cloned a recipe");

                // toast, with a link to the recipe, if only one recipe was created
                toast.success(
                  <div>
                    {recipeIDs.length > 1 ? (
                      <p>
                        {recipeIDs.length} recipes have been created. You can
                        view and edit them from the recipes page.
                      </p>
                    ) : (
                      <p>
                        <span className="font-normal">
                          {templateRecipe[0].name}
                        </span>{" "}
                        has been created. You can view and edit it from the
                        recipes page.
                      </p>
                    )}
                    {recipeIDs.length === 1 ? (
                      <Link
                        prefetch={false}
                        href={`/recipes?folder=${recipeIDs[0].folderID || ""}#${
                          recipeIDs[0].id
                        }`}
                        className="btn btn-sm btn-subtle"
                      >
                        View Recipe
                      </Link>
                    ) : null}
                  </div>
                );

                reset();

                if (onCompleted) {
                  onCompleted();
                }
              }}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

interface RecipeCardProps {
  recipe: AllRecipesQuery["allRecipes"][number];
  setSelectedRecipe: React.Dispatch<React.SetStateAction<string | null>>;
  setTemplateRecipe: (newValue: PartialRecipe[]) => void;
  amountNumberFormat: string;
  className?: string;
}

const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  setSelectedRecipe,
  setTemplateRecipe,
  amountNumberFormat,
  className,
}) => {
  const client = useClient();
  const user = useContext(UserContext);
  const { ingredients } = useContext(IngredientsContext);

  const {
    id,
    school,
    name,
    image,
    source,
    servings,
    method,
    recipeIngredients,
  } = recipe;

  const recipeSections = getRecipeSections(recipe);

  return (
    <div
      key={id}
      className={classNames(
        "group/card relative max-w-xl grow overflow-hidden",
        className || ""
      )}
    >
      <div
        className={classNames(
          "relative h-80 w-full rounded-lg bg-gradient-to-tr from-cyan-100 to-green-100 bg-cover bg-center",
          image ? "" : "border-2 border-cyan-300"
        )}
        style={image ? { backgroundImage: `url(${image})` } : {}}
      >
        {/* <div className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full bg-white/25 opacity-0 transition group-hover/card:scale-150 group-hover/card:bg-white/80 group-hover/card:opacity-100">
                  <CheckIcon className="w-12 text-green-500" />
                </div> */}

        <small className="absolute left-2 top-2 flex items-center justify-center gap-2 rounded-md bg-white/75 p-1 font-normal">
          {school.image ? (
            <img src={school.image} alt={school.name} className="h-4" />
          ) : (
            <AcademicCapIcon className="w-4" />
          )}
          {school.name}
        </small>
      </div>
      <div className="pt-2">
        <h3 className="line-clamp-2 group-hover/card:text-green-600">{name}</h3>
      </div>

      {/* Hover Card */}
      <div className="absolute left-0 top-0 h-80 w-full space-y-2 overflow-scroll rounded-lg border border-green-600 bg-white p-2 opacity-0 shadow-inner backdrop-blur transition group-hover/card:opacity-100">
        <div className="">
          <h4 className="text-base font-bold text-gray-800">Ingredients</h4>
          {recipeSections.map((section) => (
            <div key={section.id}>
              {section.id !== "sectionLess" ? (
                <h5 className="text-left text-base font-medium text-gray-600">
                  {section.name}
                </h5>
              ) : (
                <h5 className="text-left text-base font-medium text-gray-600">
                  Default Section
                </h5>
              )}

              <ul className="space-y-1 pl-2">
                {section.recipeIngredients.map(
                  ({
                    id,
                    amount,
                    unit: defaultUnit,
                    preparation,
                    ingredient: {
                      isDefault,
                      name,
                      note,
                      density,
                      measurement,
                      units: customUnits,
                    },
                  }) => {
                    const units = combineUnits({
                      measurement,
                      units: customUnits,
                      density,
                    });
                    const unit = getUnitOrFallback(units, defaultUnit);
                    const relativeAmount = unit
                      ? formatAmount(
                          amountNumberFormat,
                          toRelativeAmount(
                            amount,
                            unit,
                            density || undefined,
                            servings
                          )
                        )
                      : 0;

                    if (!unit) return null;

                    return (
                      <li key={id} className="space-x-1">
                        <span>{name}</span>
                        {preparation ? <small>({preparation})</small> : null}
                        {relativeAmount && relativeAmount !== "0" ? (
                          <small>
                            ({relativeAmount}
                            {` ${unit.name}`})
                          </small>
                        ) : null}
                      </li>
                    );
                  }
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="">
          <h4 className="text-base font-bold text-gray-800">Method</h4>
          <TextContentWrapper
            className="markdown m-1 rounded-lg border border-solid border-gray-300 bg-slate-100 p-1 shadow-lg"
            dangerouslySetInnerHTML={{
              __html:
                method
                  ?.split(/\n/)
                  .map((value, index, arr) =>
                    arr.length > 1 ? `<p>${value}</p>` : value
                  )
                  .join("") || "No Method",
            }}
          />
        </div>
      </div>

      <button
        className="btn btn-primary absolute bottom-2 right-2 opacity-0 transition-opacity group-hover/card:opacity-100"
        onClick={async () => {
          setSelectedRecipe(id);
          const tempTemplateRecipe = await mapify(
            user,
            recipe,
            ingredients,
            client
          );

          setTemplateRecipe(tempTemplateRecipe);
        }}
      >
        Select
      </button>
    </div>
    // <div className="group relative w-64 rounded-lg bg-white p-3 text-left shadow-md transition duration-150 ease-in-out hover:shadow-lg">

    //   <button
    //     className="btn absolute right-3 top-3 opacity-0 transition group-hover:opacity-100"
    //     onClick={async () => {
    //       setSelectedRecipe(recipe.id);
    //       const tempTemplateRecipe = await mapify(recipe);

    //       setTemplateRecipe(tempTemplateRecipe);
    //     }}
    //   >
    //     Select
    //   </button>

    //   <div
    //     className="h-52 w-full rounded-lg bg-gradient-to-tr from-cyan-100 to-green-100 bg-cover shadow-lg"
    //     style={image ? { backgroundImage: `url(${image})` } : {}}
    //   />

    //   <h3 title={name} className="my-3 line-clamp-2 font-semibold">
    //     {name}
    //   </h3>
    //   {source ? (
    //     <p>
    //       <a href={source} target="_blank" rel="noreferrer">
    //         <LinkIcon className="w-5 text-gray-600" />{" "}
    //         {ellipsise(source.replace(/^(https?:\/\/)?(www\.?)?/, ""), 25)}
    //       </a>
    //     </p>
    //   ) : null}
    //   <p>
    //     <AcademicCapIcon className="w-5 text-gray-600" /> {school.name}
    //   </p>
    //   <p>
    //     <DishIcon className="w-5 text-gray-600" /> {servings} Servings
    //   </p>
    //   <details className="mt-5">
    //     <summary>{recipeIngredients.length} Ingredients</summary>
    //     <ul>
    //       {recipeIngredients.map((i) => (
    //         <li key={i.id}>{i.ingredient.name}</li>
    //       ))}
    //     </ul>
    //   </details>
    // </div>
  );
};

export default CloneRecipe;
